export function parseNumericValue(value: string): number {
  if (!value || typeof value !== 'string') {
    return 0;
  }

  const cleaned = value.trim().replace(/[^\d,.-]/g, '');
  
  if (!cleaned) {
    return 0;
  }

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  
  let normalizedValue: string;
  
  if (lastComma > lastDot) {
    normalizedValue = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    normalizedValue = cleaned.replace(/,/g, '');
  } else {
    if (cleaned.includes(',')) {
      normalizedValue = cleaned.replace(',', '.');
    } else {
      normalizedValue = cleaned;
    }
  }

  const result = Number.parseFloat(normalizedValue);
  return isNaN(result) || !isFinite(result) ? 0 : result;
}


export function calculateMonthlyRate(rate: string, type: string): number {
  const parsedRate = parseNumericValue(rate);
  
  if (parsedRate <= 0) {
    return 0;
  }
  
  const annualRate = parsedRate / 100;
  
  if (type === "anual") {
    return Math.pow(1 + annualRate, 1 / 12) - 1;
  } else {
    return annualRate;
  }
}


export function calculateTimePeriod(months: number) {
  return {
    years: Math.floor(months / 12),
    months: months % 12,
    totalMonths: months
  };
}


export function roundToPrecision(value: number, decimals: number = 2): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}


export function runMonthlyGrowthSimulation(
  initial: number,
  monthlyAporte: number,
  rate: number,
  targetAmount: number,
  maxMonths: number
) {
  let accumulated = initial;
  let totalInvested = initial;
  let totalInterest = 0;
  let months = 0;

  const monthlyResult: number[] = [];
  const monthlyInvested: number[] = [initial];
  const monthlyTax: number[] = [];
  const monthlyAcumulatedInterest: number[] = [];

  while (accumulated < targetAmount && months < maxMonths) {
    const monthlyInterest = accumulated * rate;
    totalInterest += monthlyInterest;
    
    accumulated = roundToPrecision(accumulated + monthlyInterest + monthlyAporte);
    totalInvested = roundToPrecision(totalInvested + monthlyAporte);
    months++;

    monthlyTax.push(roundToPrecision(monthlyInterest));
    monthlyResult.push(roundToPrecision(totalInterest));
    monthlyInvested.push(roundToPrecision(totalInvested));
    monthlyAcumulatedInterest.push(roundToPrecision(accumulated));
  }

  return {
    months,
    accumulated,
    totalInvested,
    totalInterest,
    monthlyResult,
    monthlyInvested,
    monthlyTax,
    monthlyAcumulatedInterest
  };
}


export function calculatePaymentWithInterest(
  start: number,
  rate: number,
  months: number,
  targetAmount: number
): { isValid: boolean; payment?: number; totalInvested?: number; totalInterest?: number; message?: string } {
  
  const futureValueOfInitial = start * Math.pow(1 + rate, months);
  const denominator = ((Math.pow(1 + rate, months) - 1) / rate);
  const monthlyPayment = (targetAmount - futureValueOfInitial) / denominator;

  if (monthlyPayment <= 0) {
    return {
      isValid: false,
      message: "Com o valor inicial informado, você já atingiria a meta!"
    };
  }

  const totalInvested = start + (monthlyPayment * months);
  const totalInterest = targetAmount - totalInvested;

  return {
    isValid: true,
    payment: monthlyPayment,
    totalInvested,
    totalInterest
  };
}


export function calculateZeroRatePayment(
  start: number, 
  months: number, 
  targetAmount: number
): { isValid: boolean; payment?: number; message?: string } {
  
  const monthlyPayment = (targetAmount - start) / months;
  
  if (monthlyPayment <= 0) {
    return {
      isValid: false,
      message: "Com o valor inicial informado, você já atingiria a meta!"
    };
  }

  return {
    isValid: true,
    payment: monthlyPayment
  };
}


export function formatCalculationResults(results: number[]): number[] {
  return results.map(value => roundToPrecision(value));
}